import * as React from "react"
import { bodyW } from "../../pptx-configuration/constants"
import * as CommonUtils from "../../utils"
import * as Conf from "../../../configuration"
import * as CashFlowDomain from "../../../../../domain/sfp/cash-flow-domain"

export default class CashFlowTable extends React.Component<Props> {
	private readonly LABEL_CELL_WIDTH: number
	private readonly VALUE_CELL_WIDTH: number

	constructor(props: Readonly<Props>) {
		super(props)

		this.LABEL_CELL_WIDTH = CommonUtils.toInches(6.3)
		this.VALUE_CELL_WIDTH = (bodyW - this.LABEL_CELL_WIDTH) / props.years.length
	}

	render() {
		const tableConf = this.props.tableConfiguration
		const years = this.props.years

		return <table id={this.props.id}>
			<thead>
				<tr>
					<th data-pptx-width={this.LABEL_CELL_WIDTH}
					style={{ ...tableConf.header }}></th>

					{years.map((y, yi) =>
						<th data-pptx-width={this.VALUE_CELL_WIDTH}
						key={yi}
						style={{ ...tableConf.header }}>{y}</th>
					)}
				</tr>
			</thead>

			{this.renderGrossCashFlowTBody()}
			{this.renderCafEpargne()}
			{this.renderNetCashFlowTBody()}
			{this.renderRatiosTBody()}
		</table>
	}

	private getDisplayedValue(value: number, type?: RowType): React.ReactText {
		const locale = this.props.locale

		if ("duration" === type) {
			return CashFlowDomain.formatDeleveragingYears(value)
		}

		if ("percentage" === type) {
			const formatter = new Intl.NumberFormat(locale, { style: "percent" })
			return formatter.format(value)
		}

		const currency = this.props.currency
		const roundness = this.props.roundness

		return CommonUtils.formatCurrency(locale, currency, value, roundness)
	}

	private getRowBaseStyle(row: TableRow): React.CSSProperties {
		if (isSubtotalTableRow(row)) return this.props.tableConfiguration.subtotal
		if (isTotalTableRow(row)) return this.props.tableConfiguration.total
		if (isRatioTableRow(row)) return this.props.tableConfiguration.ratio
		return this.props.tableConfiguration.common
	}

	private renderGrossCashFlowTBody(): React.ReactNode {
		const rows: TableRow[] = [
			{
				label: "Recettes de fonctionnement",
				values: this.props.operationReceipts
			},
			{
				label: "Dépenses de fonctionnement",
				values: this.props.operationExpenses
			},
			{
				label: "Résultat de l'année",
				subtotal: true,
				values: this.props.operationResult
			},
			{
				label: "Capacité d'autofinancement brute",
				total: true,
				values: this.props.grossCashFlows
			}
		]


		return <tbody>
			{rows.map(
			(row, rowIndex) =>
				this.props.enableHideZeroLines ?	CommonUtils.isArrayAllValuesZero(row.values) === false && this.renderRow(row, rowIndex) : this.renderRow(row, rowIndex)
			)}
      	</tbody>
	}

	private renderNetCashFlowTBody(): React.ReactNode {
		const rows: TableRow[] = [
			{
				label: "Remboursement capital emprunts",
				values: this.props.reimbursedCapitals
			},
			{
				label: "Capacité d'autofinancement nette",
				total: true,
				values: this.props.netCashFlows
			},
		]

		return <tbody>
			{rows.map(
			(row, rowIndex) =>
				this.props.enableHideZeroLines ?	CommonUtils.isArrayAllValuesZero(row.values) === false && this.renderRow(row, rowIndex) : this.renderRow(row, rowIndex)
			)}
      	</tbody>
	}

	private renderRatiosTBody(): React.ReactNode {
		const rows: TableRow[] = [
			{
				label: "% remboursement CAF par la dette",
				ratio: true,
				type: "percentage",
				values: this.props.debtRefunds
			},
			{
				label: "Capital restant dû",
				subtotal: true,
				values: this.props.remainingCapitals
			},
			{
				label: "Désendettement année CAF",
				subtotal: true,
				type: "duration",
				values: this.props.deleveragingYears
			},
		]

		return <tbody>
			{rows.map(
			(row, rowIndex) =>
				this.props.enableHideZeroLines ?	CommonUtils.isArrayAllValuesZero(row.values) === false && this.renderRow(row, rowIndex) : this.renderRow(row, rowIndex)
			)}
      	</tbody>
	}

	private renderCafEpargne(): React.ReactNode {
		const rows: TableRow[] = [
			{
				label: "CAF Brute en pourcentage de RRF",
				ratio:true,
				type: "percentage",
				values: this.props.cafBruteRrf
			},
			{
				label: "Epargne de Gestion Courante",
				values: this.props.epargneGestionCourante
			}
		]


		return <tbody>
			{rows.map(
			(row, rowIndex) =>
				this.props.enableHideZeroLines ?	CommonUtils.isArrayAllValuesZero(row.values) === false && this.renderRow(row, rowIndex) : this.renderRow(row, rowIndex)
			)}
      	</tbody>
	}

	private renderRow(row: TableRow, key?: number): React.ReactNode {
		const years = this.props.years

		const conf = this.props.tableConfiguration
		const baseStyle: React.CSSProperties = this.getRowBaseStyle(row)

		return <tr key={key}>
			<td style={{ ...baseStyle }}>{row.label}</td>
			{years.map((_y, yearIndex) => {
				const value = row.values[yearIndex]
				return <td key={yearIndex} style={{ ...baseStyle, ...conf.numeric }}>
					{this.getDisplayedValue(value, row.type)}
				</td>
			})}
		</tr>
	}
}

export interface Props {
	currency: string
	debtRefunds: number[]
	deleveragingYears: number[]
	grossCashFlows: number[]
	id: string
	locale: string
	netCashFlows: number[]
	operationExpenses: number[]
	operationReceipts: number[]
	operationResult: number[]
	reimbursedCapitals: number[]
	remainingCapitals: number[]
	cafBruteRrf: number[]
	epargneCourante:number[]
	epargneGestion: number[]
	epargneGestionCourante: number[]
	roundness: Conf.RoundnessValue
	enableHideZeroLines: boolean;
	tableConfiguration: Conf.TableConfiguration
	years: number[]
}

function isSubtotalTableRow(row: TableRow): row is SubtotalTableRow {
	return undefined !== (row as SubtotalTableRow).subtotal
}

function isTotalTableRow(row: TableRow): row is TotalTableRow {
	return undefined !== (row as TotalTableRow).total
}

function isRatioTableRow(row:TableRow):row is RatioTableRow{
	return undefined !== (row as RatioTableRow).ratio;

}

interface BaseTableRow {
	label: string
	values: number[]
	type?: RowType
}

interface SubtotalTableRow extends BaseTableRow {
	subtotal: boolean
}

interface TotalTableRow extends BaseTableRow {
	total: boolean
}

interface RatioTableRow extends BaseTableRow{
	ratio:boolean;
}

type RowType = "currency" | "duration" | "percentage"

type TableRow = BaseTableRow | SubtotalTableRow | TotalTableRow | RatioTableRow



// WEBPACK FOOTER //
// ./src/component/management-report/generator/observation-generator/autofinancing-generator/cash-flow-table.tsx